import { useMemo } from 'react'
import { Container } from '@/components/ui/container'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
	OpsVettingCardBodyContainer,
	OpsVettingCardContainer,
	OpsVettingCardGrid,
	OpsVettingGalleryGrid,
	OpsVettingGalleryItemsContainer,
	OpsVettingHero,
	OpsVettingQuestionsGrid,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'

// Hero Section Skeleton
const ExperienceVettingCriteriaHeroSkeleton = () => {
	return (
		<OpsVettingHero fullWidth justify='space-between'>
			<Skeleton height={30} width={200} />
			<div className='flex items-center gap-4'>
				<div className='flex items-center gap-2'>
					<Skeleton height={20} width={20} />
					<Skeleton height={20} width={100} />
				</div>
				<Skeleton height={38} width={100} />
			</div>
		</OpsVettingHero>
	)
}

// Expertise Section Skeleton
const ExperienceVettingCriteriaExpertiseSkeleton = () => {
	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<div className='flex items-center gap-8'>
					<OpsVettingSectionTitle>
						<Skeleton height={28} width={150} />
					</OpsVettingSectionTitle>
					<div className='flex items-center gap-2'>
						<Skeleton height={36} width={36} />
						<Skeleton height={36} width={36} />
						<Skeleton height={36} width={36} />
					</div>
				</div>
				<Skeleton height={24} width={140} />
			</div>

			<Separator orientation='horizontal' />

			<OpsVettingQuestionsGrid>
				<div className='flex w-full flex-col gap-6'>
					{[1, 2, 3, 4, 5, 6, 7].map((n) => (
						<div className='flex flex-col gap-2' key={n}>
							<Skeleton height={20} width={140} />
							<Skeleton height={24} width='100%' />
						</div>
					))}
				</div>
				<Separator orientation='vertical' />
				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						<Skeleton height={20} width={180} />
						<Skeleton
							height={56}
							style={{ borderRadius: '50%' }}
							width={56}
						/>
					</div>
					{[1, 2, 3, 4].map((n) => (
						<div className='flex flex-col gap-2' key={n}>
							<Skeleton height={20} width={140} />
							<Skeleton height={24} width='100%' />
						</div>
					))}
				</div>
			</OpsVettingQuestionsGrid>
		</OpsVettingSectionWrapper>
	)
}

// Itinerary Section Skeleton
const ExperienceVettingCriteriaItinerarySkeleton = () => {
	// Generate an array of items for the itinerary cards
	const skeletonKeys = useMemo(
		() => Array.from({ length: 2 }, (_, i) => `skeleton-${i}`),
		[]
	)

	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<OpsVettingSectionTitle>
					<Skeleton height={24} width={200} />
				</OpsVettingSectionTitle>
				<Skeleton height={20} width={140} />
			</div>
			<Separator orientation='horizontal' />
			<OpsVettingCardGrid>
				{skeletonKeys.map((key) => (
					<OpsVettingCardContainer
						direction='column'
						gap={2}
						key={key}
					>
						<div className='flex w-full items-center justify-between gap-2'>
							<div className='flex items-center gap-8'>
								<Skeleton height={22} width={150} />
								<div className='flex gap-2'>
									<Skeleton height={36} width={36} />
									<Skeleton height={36} width={36} />
									<Skeleton height={36} width={36} />
								</div>
							</div>
						</div>

						<OpsVettingCardBodyContainer>
							<div className='flex w-full flex-col gap-2.5'>
								{[1, 2, 3, 4].map((n) => (
									<div
										className='flex w-full flex-col gap-2'
										key={n}
									>
										<Skeleton height={20} width={120} />
										<Skeleton height={20} width='100%' />
									</div>
								))}
							</div>

							<Skeleton
								height={200}
								style={{
									borderRadius: '8px',
									aspectRatio: '1/1'
								}}
								width='100%'
							/>
						</OpsVettingCardBodyContainer>
					</OpsVettingCardContainer>
				))}
			</OpsVettingCardGrid>
		</OpsVettingSectionWrapper>
	)
}

// Gallery Section Skeleton
const ExperienceVettingCriteriaGallerySkeleton = () => {
	// Generate an array of items for the gallery
	const skeletonItems = useMemo(
		() => Array.from({ length: 9 }, (_, i) => `gallery-skeleton-${i}`),
		[]
	)

	return (
		<OpsVettingSectionWrapper direction='column' fullWidth gap={6}>
			{/* Header section */}
			<div className='flex w-full items-center justify-between'>
				<div className='flex items-center gap-8'>
					{/* Title skeleton */}
					<Skeleton height={24} width={180} />

					{/* Upload button skeleton */}
					<Skeleton height={36} width={120} />

					{/* Checkbox skeletons */}
					<div className='flex items-center gap-2'>
						<Skeleton height={20} width={20} />
						<Skeleton height={20} width={150} />
					</div>

					<div className='flex items-center gap-2'>
						<Skeleton height={20} width={20} />
						<Skeleton height={20} width={180} />
					</div>
				</div>

				{/* Status section */}
				<Skeleton height={20} width={140} />
			</div>

			<Separator orientation='horizontal' />

			{/* Gallery grid */}
			<OpsVettingGalleryGrid>
				{/* Cover photo skeleton (first item, slightly different) */}
				<OpsVettingGalleryItemsContainer direction='column' gap={4}>
					<Skeleton
						height='100%'
						style={{ borderRadius: '8px', aspectRatio: '1/1' }}
						width='100%'
					/>
					<div className='flex w-full flex-col gap-2'>
						<Skeleton height={18} width='70%' />
					</div>
				</OpsVettingGalleryItemsContainer>

				{/* Regular gallery items */}
				{skeletonItems.map((key) => (
					<OpsVettingGalleryItemsContainer
						direction='column'
						gap={4}
						key={key}
					>
						<Skeleton
							height='100%'
							style={{ borderRadius: '8px', aspectRatio: '1/1' }}
							width='100%'
						/>
						<div className='flex w-full flex-col gap-2'>
							<Skeleton height={18} />
							<div className='flex gap-2'>
								<Skeleton height={38} />
								<Skeleton height={38} />
							</div>
						</div>
					</OpsVettingGalleryItemsContainer>
				))}
			</OpsVettingGalleryGrid>
		</OpsVettingSectionWrapper>
	)
}

// Rejected Images Section Skeleton

// Main Skeleton Component for the entire page
export const ExperienceVettingCriteriaSkeleton = () => {
	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			<Container fullWidth>
				<ExperienceVettingCriteriaHeroSkeleton />
			</Container>

			<Container fullWidth>
				<div className='flex w-full flex-col gap-6'>
					<ExperienceVettingCriteriaExpertiseSkeleton />
					<ExperienceVettingCriteriaItinerarySkeleton />
					<ExperienceVettingCriteriaGallerySkeleton />
				</div>
			</Container>
		</ScrollArea>
	)
}

ExperienceVettingCriteriaSkeleton.displayName =
	'ExperienceVettingCriteriaSkeleton'
